import { useState } from 'react';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';

import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../components/ui/form';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  WHITE_LABEL_COMMISSION_PLANS,
  WHITE_LABEL_FEATURE_FIELDS,
  WHITE_LABEL_STATUSES,
} from '../../lib/whiteLabelValidation';
import { cn } from '../../lib/utils';

const BRAND_FIELDS = (mode) => [
  { label: 'Brand Name', name: 'brandName', type: 'text', placeholder: 'Acme Finance' },
  { label: 'Owner Name', name: 'ownerName', type: 'text', placeholder: 'Rohan Sharma' },
  { label: 'WL Admin Name', name: 'adminName', type: 'text', placeholder: 'Priya Mehta' },
  { label: 'WL Admin Email', name: 'adminEmail', type: 'email', placeholder: 'admin@example.com' },
  {
    label: mode === 'create' ? 'WL Admin Password' : 'New WL Admin Password',
    name: mode === 'create' ? 'adminPass' : 'adminPassword',
    type: 'password',
    placeholder: 'Use a strong password',
    description:
      mode === 'create'
        ? 'Minimum 8 characters with uppercase, lowercase, number, and special character.'
        : 'Leave blank to keep the current password. If set, use at least 8 characters with uppercase, lowercase, number, and special character.',
  },
  { label: 'Phone Number', name: 'phone', type: 'tel', placeholder: '+919876543210', description: 'Optional. 10 to 15 digits, with an optional leading +.' },
  { label: 'Custom Domain', name: 'domain', type: 'text', placeholder: 'portal.example.com', description: 'Optional. Enter only the domain, without http:// or https://.' },
];

function TextField({ control, field, disabled }) {
  const [showPassword, setShowPassword] = useState(false);
  const autoComplete =
    field.name === 'adminEmail'
      ? 'email'
      : field.name === 'phone'
        ? 'tel'
        : field.type === 'password'
          ? 'new-password'
          : 'off';

  return (
    <FormField
      control={control}
      name={field.name}
      render={({ field: inputField }) => (
        <FormItem>
          <FormLabel>{field.label}</FormLabel>
          <FormControl>
            <div className="relative">
              <Input
                {...inputField}
                type={field.type === 'password' && showPassword ? 'text' : field.type}
                value={inputField.value ?? ''}
                placeholder={field.placeholder}
                autoComplete={autoComplete}
                disabled={disabled}
                className={field.type === 'password' ? 'pr-10' : undefined}
              />
              {field.type === 'password' ? (
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  disabled={disabled}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              ) : null}
            </div>
          </FormControl>
          {field.description ? <FormDescription>{field.description}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function ColorField({ control, name, label, disabled }) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                disabled={disabled}
                className={cn(
                  'h-10 w-14 rounded-md border border-input bg-background p-1 cursor-pointer',
                  disabled && 'cursor-not-allowed opacity-50'
                )}
              />
              <Input value={field.value || ''} onChange={field.onChange} onBlur={field.onBlur} disabled={disabled} className="font-mono uppercase" />
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function SelectField({ control, name, label, options, placeholder, disabled }) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function WhiteLabelForm({
  form,
  mode,
  pricingPlans,
  onSubmit,
  onCancel,
  submitLabel,
  submitting,
  submitError,
}) {
  const featurePermissionsError = form.formState.errors.featurePermissions?.message;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="rounded-xl border p-6 shadow-sm space-y-5 noValidate">
        {submitError ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        ) : null}

        <h2 className="font-semibold text-base border-b pb-3">Brand Information</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {BRAND_FIELDS(mode).map((field) => (
            <TextField key={field.name} control={form.control} field={field} disabled={submitting} />
          ))}
        </div>

        <h2 className="font-semibold text-base border-b pb-3 pt-2">Branding</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <ColorField control={form.control} name="primaryColor" label="Primary Color" disabled={submitting} />
          <ColorField control={form.control} name="secondaryColor" label="Secondary Color" disabled={submitting} />
          <SelectField
            control={form.control}
            name="commissionPlan"
            label="Commission Plan"
            options={WHITE_LABEL_COMMISSION_PLANS}
            placeholder="Select a commission plan"
            disabled={submitting}
          />

          <FormField
            control={form.control}
            name="pricingPlanId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pricing Plan</FormLabel>
                <Select value={field.value || '__none__'} onValueChange={(value) => field.onChange(value === '__none__' ? '' : value)} disabled={submitting}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a pricing plan" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="__none__">No plan</SelectItem>
                    {pricingPlans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>Optional. Assign a pricing plan to control workspace access.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <SelectField
            control={form.control}
            name="status"
            label="Status"
            options={WHITE_LABEL_STATUSES}
            placeholder="Select a status"
            disabled={submitting}
          />
        </div>

        <h2 className="font-semibold text-base border-b pb-3 pt-2">Feature Permissions</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {WHITE_LABEL_FEATURE_FIELDS.map((item) => (
            <FormField
              key={item.key}
              control={form.control}
              name={`featurePermissions.${item.key}`}
              render={({ field }) => (
                <FormItem className="space-y-0">
                  <FormControl>
                    <label
                      htmlFor={`feature-${item.key}`}
                      className={cn(
                        'flex items-center gap-3 rounded-lg border p-3 transition-colors',
                        !submitting && 'cursor-pointer hover:bg-accent/30'
                      )}
                    >
                      <Checkbox
                        id={`feature-${item.key}`}
                        checked={Boolean(field.value)}
                        onCheckedChange={(checked) => field.onChange(Boolean(checked))}
                        disabled={submitting}
                      />
                      <span className="text-sm font-medium">{item.label}</span>
                    </label>
                  </FormControl>
                </FormItem>
              )}
            />
          ))}
        </div>
        {featurePermissionsError ? (
          <p className="text-[0.8rem] font-medium text-destructive">{featurePermissionsError}</p>
        ) : null}

        <div className="flex gap-3 pt-2">
          <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white" disabled={submitting}>
            {submitting ? (mode === 'create' ? 'Creating...' : 'Saving...') : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
